import React from 'react';

import { Tile, TileAction, Dropdown, MenuItem, Table, Row, Cell } from '@dsi/react-eds';

import Example from '../components/Example';

const codeSample1 = `<Tile
  id="tile-3"
  className="sm-12"
  title="Tile of basic"
  subtitle="Subtitle example"
>
  <div className="box"></div>
</Tile>`;

const codeSample2 = `<Tile
  id="tile-5"
  className="sm-12"
  title="Tile with actions"
  actions={[
    <TileAction>
      <i key="reload" className="icon icon-reload" />
    </TileAction>,
    <TileAction>
      <Dropdown key="actions" more>
        <MenuItem value={0}>Some action 1</MenuItem>
        <MenuItem value={1}>Some action 2</MenuItem>
        <MenuItem value={2}>Some action 3</MenuItem>
      </Dropdown>
    </TileAction>
  ]}
  >
    <div className="box"></div>
</Tile>`;

const codeSample3 = `<Tile
  id="tile-7"
  className="sm-12"
  title="Tile with fullscreen action"
  onMaximize={() => {
    // Tile is maximized
  }}
  onMinimize={() => {
    // Tile is minimized
  }}
  actions={[
    <TileAction key="maximize" maximize />,
  ]}
>
  <div className="box" />
</Tile>`;

const codeSample4 = `<Tile
  id="tile-9"
  className="sm-12"
  title="Content of tile without margins"
  subtitle="Subtitle"
  margins="no-margins"
  >
    <div className="box"></div>
</Tile>`;

const TilesPage = () => {
  const columns = [
    { title: 'Prop', attribute: 'col1', width: '140px' },
    { title: 'Type', attribute: 'col2', width: '200px' },
    { title: 'Default', attribute: 'col3', width: '100px' },
    { title: 'Description', attribute: 'col4' },
  ];

  return (
    <>
      <div className="row">
        <Tile id="tile-1" title="Overview">
          <div className="column sm-12 xl-8">
            <p>On the left side, each tile contains a header. On the right side, the tile may contain actions and/or settings. There are four possible scenarios:</p>
            <ul>
              <li>No actions, no need to place any Button.</li>
              <li>Only one main action needed (e.g. “full screen”): Use the corresponding icon + tooltip.</li>
              <li>Only secondary actions needed: In this case all actions will be grouped under the “more” icon-button, and actions will be shown in a dropdown menu.</li>
              <li>One primary action, and multiple secondary actions. In this case the primary action and the “more” icon-button should be displayed side by side.</li>
              <li>Some components will need more actions that affect the whole component found inside a specific tile. Such actions should be added on a second layer and using text-buttons.</li>
            </ul>
            <div className="section">Props</div>
            <Table compact columns={columns}>
              <Row>
                <Cell><kbd>id</kbd></Cell>
                <Cell>String</Cell>
                <Cell>Required</Cell>
                <Cell>Tile id.</Cell>
              </Row>
              <Row>
                <Cell><kbd>title</kbd></Cell>
                <Cell>String</Cell>
                <Cell>'Title'</Cell>
                <Cell>Tile title.</Cell>
              </Row>
              <Row>
                <Cell><kbd>subtitle</kbd></Cell>
                <Cell>String</Cell>
                <Cell>''</Cell>
                <Cell>Tile subtitle.</Cell>
              </Row>
              <Row>
                <Cell><kbd>margins</kbd></Cell>
                <Cell>String</Cell>
                <Cell>null</Cell>
                <Cell><span>Tile margins. Possible values are <kbd>no-margins</kbd>, <kbd>no-right-margin</kbd>, <kbd>no-left-margin</kbd></span></Cell>
              </Row>
              <Row>
                <Cell><kbd>actions</kbd></Cell>
                <Cell>TileAction, Array[TileAction]</Cell>
                <Cell>[]</Cell>
                <Cell>Tile actions.</Cell>
              </Row>
              <Row>
                <Cell><kbd>onMaximize</kbd></Cell>
                <Cell>Function</Cell>
                <Cell>no-op</Cell>
                <Cell>Called when the tile is maximized.</Cell>
              </Row>
              <Row>
                <Cell><kbd>onMinimize</kbd></Cell>
                <Cell>Function</Cell>
                <Cell>no-op</Cell>
                <Cell>Called when the tile is minimized.</Cell>
              </Row>
              <Row>
                <Cell><kbd>children</kbd></Cell>
                <Cell>Element, Array[Element]</Cell>
                <Cell>null</Cell>
                <Cell>Tile content.</Cell>
              </Row>
            </Table>
            <p>All other props are passed as attributes to the tile div element.</p>
            <div className="section">Props TileAction</div>
            <Table compact columns={columns}>
              <Row>
                <Cell><kbd>maximize</kbd></Cell>
                <Cell>Boolean</Cell>
                <Cell>false</Cell>
                <Cell>Maximize tile action.</Cell>
              </Row>
              <Row>
                <Cell><kbd>children</kbd></Cell>
                <Cell>Element</Cell>
                <Cell>null</Cell>
                <Cell>Custom tile action.</Cell>
              </Row>
            </Table>
          </div>
        </Tile>
      </div>
      <div className="row">
        <Tile id="tile-2" title="Basic">
          <div className="column sm-12 xl-8">
            <p>All tiles contains a header and a content. The header is divided into left and right. On the left, there should always be a title set, (being the subtitle optional). On the right, only the icon actions with their tooltip should be added as explained in the overview.</p>
            <p>The content in the example below contains a box class. This is just for demo purposes, do not use and replace with proper content instead. The content height will expand the tile and set the height of the row.</p>
            <Example fullwidth code={codeSample1} />
          </div>
        </Tile>
      </div>
      <div className="row">
        <Tile id="tile-3" className="sm-12" title="Tile of basic" subtitle="Subtitle example">
          <div className="box" />
        </Tile>
      </div>
      <div className="row">
        <Tile id="tile-4" title="With actions">
          <div className="column sm-12 xl-8">
            <p>An example of a tile with the main action (reload) and secondary actions under the "more" dropdown-menu.</p>
            <div className="section">Implementation</div>
            <ul>
              <li>Use the {'<TileAction>'} component to wrap any action.</li>
              <li>Pass all actions as an array to the <kbd>actions</kbd> prop.</li>
            </ul>
            <Example fullwidth code={codeSample2} />
          </div>
        </Tile>
      </div>
      <div className="row">
        <Tile
          id="tile-5"
          className="sm-12"
          title="Tile with actions"
          actions={[
            <TileAction key="reload">
              <i className="icon icon-reload" />
            </TileAction>,
            <TileAction key="actions">
              <Dropdown more>
                <MenuItem value={0}>Some action 1</MenuItem>
                <MenuItem value={1}>Some action 2</MenuItem>
                <MenuItem value={2}>Some action 3</MenuItem>
              </Dropdown>
            </TileAction>,
          ]}
        >
          <div className="box" />
        </Tile>
      </div>
      <div className="row">
        <Tile id="tile-6" title="Fullscreen">
          <div className="column sm-12 xl-8">
            <p>An example of a tile with the main action fullscreen.</p>
            <div className="section">Implementation</div>
            <ul>
              <li>Set the <kbd>maximize</kbd> prop on the {'<TileAction>'} component.</li>
              <li>Use <kbd>onMaximize</kbd> and/or <kbd>onMinimize</kbd> props to be notified when the tile is maximized or minimized.</li>
            </ul>
            <Example fullwidth code={codeSample3} />
          </div>
        </Tile>
      </div>
      <div className="row">
        <Tile
          id="tile-7"
          className="sm-12"
          title="Tile with fullscreen action"
          actions={[
            <TileAction key="maximize" maximize />,
          ]}
        >
          <div className="box" />
        </Tile>
      </div>
      <div className="row">
        <Tile id="tile-8" title="Margins">
          <div className="column sm-12 xl-8">
            <p>As a rule of thumb, the tile content should always be within the margins specified. There might be some cases where you might want to maximize the content to the borders of the tile. An example is the usage of maps, where you want to increase the space available to the maximum.</p>
            <p>You can use the prop <kbd>margin</kbd> with the following values to fulfill this purpose: <kbd>no-right-margin</kbd>, <kbd>no-left-margin</kbd> and <kbd>no-margins</kbd> (this last one remove right bottom left. In all cases the top margin of the content must be respected.</p>
            <Example fullwidth code={codeSample4} />
          </div>
        </Tile>
      </div>
      <div className="row">
        <Tile
          id="tile-9"
          className="sm-12"
          title="Content of tile without margins"
          subtitle="Subtitle"
          margins="no-margins"
        >
          <div className="box" />
        </Tile>
      </div>
    </>
  );
};

export default TilesPage;
